import { useCallback, useEffect, useState } from "react";
import { FaHandSparkles } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { api } from "../../services/api";

const AJUDA = {
  titulo: "Cadastro de Tipos de Manejo",
  descricao:
    "Cadastre aqui as categorias de procedimento usadas nos Eventos de Manejo (ex.: Vacinação, Inseminação, Desmama). Esse cadastro serve de referência para classificar cada evento registrado por animal.",
  secoes: [
    {
      titulo: "Dados do Tipo de Manejo",
      campos: [
        {
          nome: "Nome do Tipo de Manejo",
          obrigatorio: true,
          explicacao: "Nome curto do procedimento (ex.: 'Vacinação', 'Desmama'), usado nas listas de seleção em Eventos de Manejo.",
        },
        {
          nome: "Descrição",
          obrigatorio: false,
          explicacao: "Detalhes do procedimento: equipamentos necessários, cuidados especiais, periodicidade recomendada.",
        },
      ],
    },
  ],
  dicas: [
    "Um tipo de manejo com eventos já registrados não pode ser excluído — o botão de excluir fica desabilitado nesse caso.",
    "Esta tela ainda não tem suporte a uso offline — cadastrar, editar ou excluir exige conexão com a internet.",
  ],
};

export default function GestaoTiposManejo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [tiposManejo, setTiposManejo] = useState([]);
  const [tiposManejoFiltrados, setTiposManejoFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
  });

  // Carregar tipos de manejo
  const carregarTiposManejo = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await api.get("/pecuaria/tipos_manejo/");
      const dados = response.data.results || response.data;
      setTiposManejo(dados);
      setTiposManejoFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar tipos de manejo:", error);
      alert("Erro ao carregar lista de tipos de manejo");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarTiposManejo();
  }, [carregarTiposManejo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome do tipo de manejo.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      descricao: formData.descricao || "",
    };

    try {
      if (editando) {
        const response = await api.put(`/pecuaria/tipos_manejo/${editandoId}/`, payload);
        if (response.status === 200) {
          alert("Tipo de manejo atualizado com sucesso!");
          handleCancel();
          carregarTiposManejo();
        }
      } else {
        const response = await api.post("/pecuaria/tipos_manejo/", payload);
        if (response.status === 201 || response.status === 200) {
          alert("Tipo de manejo cadastrado com sucesso!");
          handleCancel();
          carregarTiposManejo();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (tipo) => {
    setFormData({
      nome: tipo.nome || "",
      descricao: tipo.descricao || "",
    });
    setEditando(true);
    setEditandoId(tipo.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoTipo = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      descricao: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este tipo de manejo? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/tipos_manejo/${id}/`);
      alert("Tipo de manejo excluído com sucesso!");
      carregarTiposManejo();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir tipo de manejo. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...tiposManejo];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(t => 
        t.nome?.toLowerCase().includes(buscaLower) ||
        t.descricao?.toLowerCase().includes(buscaLower)
      );
    }
    
    setTiposManejoFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setTiposManejoFiltrados(tiposManejo);
  };

  // Truncar descrição
  const truncarDescricao = (texto, maxLength = 60) => {
    if (!texto) return "—";
    if (texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + "...";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Tipos de Manejo <FaHandSparkles size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoTipo}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Tipo de Manejo
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Tipo de Manejo" : "Cadastrar Novo Tipo de Manejo"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Nome do Tipo de Manejo *</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Ex: Vacinação, Inseminação, Desmama, etc."
                    required
                  />
                  <span className="text-xs text-gray-500 mt-1">
                    Identificação do procedimento ou prática de manejo
                  </span>
                </div>

                {/* Linha 2: Descrição */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    rows={4}
                    className="w-full max-w-xl border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Descreva detalhadamente o procedimento, equipamentos necessários, cuidados especiais, etc..."
                  />
                  <span className="text-xs text-gray-500 mt-1">
                    Informações adicionais sobre este tipo de manejo (opcional)
                  </span>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome ou Descrição</label>
                  <input
                    type="text"
                    placeholder="Digite o nome ou descrição do tipo de manejo..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Tipos de Manejo */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {tiposManejoFiltrados.length} tipo(s) de manejo encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Qtd. Registros</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiposManejoFiltrados.map((tipo) => (
                        <tr key={tipo.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{tipo.id}</td>
                          <td className="p-3 text-sm font-medium">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {tipo.nome}
                            </span>
                           </td>
                          <td className="p-3 text-sm max-w-md">{truncarDescricao(tipo.descricao)}</td>
                          <td className="p-3 text-sm text-center">
                            <span className="font-medium">{tipo.registros_count || 0}</span>
                            <span className="text-gray-500 text-xs ml-1">registros</span>
                           </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(tipo)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(tipo.id)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  tipo.registros_count > 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                                title={tipo.registros_count > 0 ? "Tipo com registros não pode ser excluído" : "Excluir"}
                                disabled={tipo.registros_count > 0}
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      ))}
                      
                      {tiposManejoFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum tipo de manejo encontrado com os filtros selecionados."
                              : "Nenhum tipo de manejo cadastrado."}
                           </td>
                         </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}